import React from 'react';
import { createUnifiedTheme, palettes, UnifiedThemeProvider } from '@backstage/theme';
import { AppTheme } from '@backstage/core-plugin-api';

// Custom header configuration
const headerShape = `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='400' fill='none'%3e%3cmask id='a' width='2269' height='1408' x='-610' y='-509' maskUnits='userSpaceOnUse'%3e%3ccircle cx='1212.8' cy='74.8' r='317.5' fill='url(%23paint0_linear)' transform='rotate(-52 1213 75)'/%3e%3ccircle cx='737.8' cy='445.8' r='317.5' fill='url(%23paint1_linear)' transform='rotate(-116 738 446)'/%3e%3ccircle cx='601.8' cy='52.8' r='418.6' fill='url(%23paint2_linear)' transform='rotate(-117 602 53)'/%3e%3ccircle cx='999.8' cy='364' r='389.1' fill='url(%23paint3_linear)' transform='rotate(31 1000 364)'/%3e%3cellipse cx='-109.2' cy='263.5' fill='url(%23paint4_linear)' rx='429.2' ry='465.8' transform='rotate(-85 -109 264)'/%3e%3c/mask%3e%3cg mask='url(%23a)'%3e%3cpath fill='white' d='M0 0h1368v400H0z'/%3e%3c/g%3e%3cdefs%3e%3clinearGradient id='paint0_linear' x1='1301.2' x2='161.4' y1='-1879.7' y2='-969.6' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint1_linear' x1='826.2' x2='-313.6' y1='-1508.7' y2='-598.6' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint2_linear' x1='718.4' x2='-784.3' y1='-2524' y2='-1324.2' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint3_linear' x1='1108.2' x2='-288.6' y1='-2031.1' y2='-915.9' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint4_linear' x1='10.4' x2='-1626.5' y1='-2603.8' y2='-1399.5' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3c/defs%3e%3c/svg%3e")`;

const toastyOrange = ['#BE2200', '#A41D00'];
const headerGradient = `linear-gradient(90deg, ${toastyOrange.join(', ')})`;
const headerBackgroundImage = `${headerShape}, ${headerGradient}`;

const muiTableCell = {
  styleOverrides: {
    root: {
      wordBreak: 'keep-all',
      overflowWrap: 'normal',
      '& > div > a': {
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: '0.875rem',
      }
    }
  }
} as any;

const backstageHeader = {
  styleOverrides: {
    header: {
      backgroundImage: headerBackgroundImage,
      color: '#FFFFFF',
      width: 'auto',
    },
  },
};

const backstagePage = {
  styleOverrides: {
    root: {
      overflowX: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }
  }
} as any;

const backstageSidebarPage = {
  styleOverrides: {
    root: {
      boxSizing: 'border-box',
      overflowX: 'hidden',
      maxWidth: '100vw',
      '& > *': {
        boxSizing: 'border-box',
        maxWidth: '100%',
      }
    }
  }
} as any;

const backstageContent = {
  styleOverrides: {
    root: {
      overflowX: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }
  }
} as any;

const backstageContentHeader = {
  styleOverrides: {
    container: {
      '& h2': {
        color: '#FFFFFF',
      }
    }
  }
} as any;

function createLightTheme(): AppTheme {
  const unifiedTheme = createUnifiedTheme({
    palette: {
      ...palettes.light,
      navigation: {
        background: '#424242',
        indicator: '#ffffff',
        color: '#b5b5b5',
        selectedColor: '#FFF',
        navItem: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
        submenu: {
          background: '#424242',
        },
      },
    },
    fontFamily: 'Red Hat Display',
    defaultPageTheme: 'home',
    components: {
      BackstageHeader: backstageHeader,
      BackstagePage: backstagePage,
      BackstageSidebarPage: backstageSidebarPage,
      BackstageContent: backstageContent,
      MuiTableCell: muiTableCell,
    },
  });

  return {
    id: 'onewelcome-light',
    title: 'Light Theme',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={unifiedTheme}>
        {children}
      </UnifiedThemeProvider>
    ),
  };
}

const styleOverridesGrayBackground = {
  root: {
    backgroundColor: '#333333',
    overflowX: 'hidden',
    maxWidth: '100vw',
    boxSizing: 'border-box',
    '& > *': {
      boxSizing: 'border-box',
      maxWidth: '100%',
    }
  },
} as any;

const muiTableCellDark = {
  styleOverrides: {
    root: {
      wordBreak: 'keep-all',
      overflowWrap: 'normal',
      '& > div > a': {
        color: 'rgb(255, 255, 255)',
        fontSize: '0.875rem',
      }
    }
  }
} as any;

const backstagePageDark = {
  styleOverrides: {
    root: {
      backgroundColor: '#333333',
      overflowX: 'hidden',
      maxWidth: '100%',
      boxSizing: 'border-box',
    }
  }
} as any;

function createDarkTheme(): AppTheme {
  const unifiedTheme = createUnifiedTheme({
    palette: {
      ...palettes.dark,
      primary: {
        ...palettes.dark.primary,
        main: '#9CC9E8',
        light: '#B8D9F0',
        dark: '#7BA7C7',
      },
      secondary: {
        ...palettes.dark.secondary,
        main: '#9CC9E8',
      },
      text: {
        primary: '#FFFFFF',
        secondary: 'rgba(255, 255, 255, 0.7)',
      },
      link: '#9CC9E8',
      navigation: {
        background: '#424242',
        indicator: '#ffffff',
        color: '#b5b5b5',
        selectedColor: '#FFF',
        navItem: {
          hoverBackground: 'rgba(255, 255, 255, 0.1)',
        },
        submenu: {
          background: '#424242',
        },
      },
    },
    fontFamily: 'Red Hat Display',
    defaultPageTheme: 'home',
    components: {
      BackstageHeader: backstageHeader,
      BackstageContentHeader: backstageContentHeader,
      BackstagePage: backstagePageDark,
      BackstageSidebarPage: {
        styleOverrides: styleOverridesGrayBackground,
      },
      BackstageContent: backstageContent,
      MuiTableCell: muiTableCellDark,
    },
  });

  return {
    id: 'onewelcome-dark',
    title: 'Dark Theme',
    variant: 'dark',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={unifiedTheme}>
        {children}
      </UnifiedThemeProvider>
    ),
  };
}

export function getThemes(): AppTheme[] {
  return [createLightTheme(), createDarkTheme()];
}
